import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IssueComment } from "@adsk/acc-issues-types";
import CommentCard from "@adsk/alloy-react-comment-card";
import { Issue, IssueViewers } from "@adsk/acc-issues-types";
import { IssueCommentsPanelHeader } from "./IssueCommentsPanelHeader";
import { IssueCommentsPanelInput } from "./IssueCommentsPanelInput";
import {
  useDispatch,
  useSelector,
} from "@adsk/acc-issues-core/state/redux/hooks";
import {
  fetchIssueViewersByIdAction,
  getIssueViewers,
} from "@adsk/acc-issues-core/state/redux/ducks/issuesViewers";
import {
  fetchIssueCommentsAction,
  getHasIssueCommentsFetchErrored,
  getHasNextIssueCommentsPage,
  getIssueComments,
  getPaginationState,
  getTotalIssueComments,
} from "@adsk/acc-issues-core/state/redux/ducks/issueComments";
import { IssueCommentBlock } from "../IssueCommentBlock";
import { useIsLoadingProjectMembers } from "@adsk/acc-issues-common/hooks/data/projectMembersHooks";
import { IssueCommentsLoader } from "./IssueCommentsLoader";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useGetProjectActorByOxygenId } from "../../hooks/useGetProjectActorByOxygenId";

export const useIssueViewers = (issueId: string): IssueViewers | undefined => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchIssueViewersByIdAction({ issueId }));
  }, [issueId, dispatch]);
  return useSelector(getIssueViewers)(issueId);
};

interface IssueCommentsProps {
  issue: Issue;
  viewOnly: boolean;
}

export const IssueCommentsPanel = React.memo(
  ({ issue, viewOnly }: IssueCommentsProps) => {
    const { T } = useTranslations();
    const dispatch = useDispatch();
    const issueViewers = useIssueViewers(issue.id);
    const nonMember = T.translate("activityLog.nonMember") as string;
    const getUser = useGetProjectActorByOxygenId();
    const comments: IssueComment[] = useSelector(getIssueComments);
    const hasNextPage = useSelector(getHasNextIssueCommentsPage);
    const totalComments = useSelector(getTotalIssueComments);
    const isLoadingProjectMembers = useIsLoadingProjectMembers();
    const isErrored = useSelector(getHasIssueCommentsFetchErrored);
    const currentPaginationState = useSelector(getPaginationState);

    // We are fetching comments only after our first render, the store might not be updated yet
    const [isLoadingComments, setIsLoadingComments] = useState(true);

    const requestMoreComments = useCallback(
      async (offset: number) => {
        setIsLoadingComments(true);
        await dispatch(
          fetchIssueCommentsAction({
            issueId: issue.id,
            offset,
          })
        );
        setIsLoadingComments(false);
      },
      [issue.id, dispatch]
    );

    useEffect(() => {
      requestMoreComments(0);
    }, [issue.id, requestMoreComments]);

    // We flip the comments direction so React renders them in the correct order
    const sortedComments = useMemo(() => [...comments].reverse(), [comments]);

    if (isLoadingComments || isLoadingProjectMembers) {
      return <IssueCommentsLoader />;
    }
    return (
      <div data-testid="comments">
        <IssueCommentsPanelHeader
          isLoading={isLoadingComments}
          isErrored={isErrored}
          shownCommentsCount={sortedComments.length}
          totalCommentsCount={totalComments}
          canRequestMoreComments={hasNextPage}
          onRequestMoreComments={requestMoreComments}
          onRetry={requestMoreComments}
          paginationState={currentPaginationState}
        />
        <div>
          {sortedComments.map(comment => {
            const userDetails = getUser(comment.createdBy);
            const user = {
              ...userDetails,
              //need to assign since user prop in comment card since it requires name to be present
              //useGetProjectActorByOxygenId can return null if oxygenId is not present
              name: userDetails?.name || nonMember,
              ...(nonMember === userDetails?.name && { defaultAvatar: true }),
            };
            return (
              <CommentCard
                key={comment.id}
                user={user}
                date={new Date(comment.clientCreatedAt)}
                comment={comment.body}
                style={{ marginBottom: 10 }}
                renderCommentText={() => (
                  <IssueCommentBlock
                    commentBody={comment.body}
                    style={{
                      padding: "12px 16px",
                      display: "block",
                      wordBreak: "break-word",
                    }}
                  />
                )}
              />
            );
          })}
        </div>
        {!viewOnly && (
          <IssueCommentsPanelInput
            issue={issue}
            issueViewers={issueViewers}
            previousComments={sortedComments}
          />
        )}
      </div>
    );
  }
);
